import { BlogSection } from '@/types/blog';

export const ei_des_kostolany: BlogSection = {
  id: 'ei-des-kostolany',
  title: 'Das Ei des Kostolany: Die Zyklen des Marktes verstehen',
  content: `
    <p>Um die Dynamik des Marktes zu veranschaulichen, entwickelte Kostolany eine simple, aber brillante Metapher: das „<strong>Ei des Kostolany</strong>". Dieses Ei beschreibt die zyklische Bewegung der Börsenkurse, die sich in bestimmten Phasen immer wiederholt.</p>

    <div class="bg-gradient-to-br from-joerg-50 to-primary/10 p-8 rounded-2xl my-10 border border-joerg-200">
      <h4 class="text-center text-xl font-bold text-joerg-800 mb-6">🥚 Das Ei des Kostolany</h4>
      
      <div class="grid md:grid-cols-3 gap-6">
        <div class="text-center">
          <div class="w-16 h-16 bg-red-100 border-4 border-red-300 rounded-full mx-auto mb-4 flex items-center justify-center">
            <span class="text-2xl">📉</span>
          </div>
          <h5 class="font-bold text-red-700 mb-2">Korrektur-Phase</h5>
          <p class="text-sm text-red-600">Angst dominiert, „schwache Hände" verkaufen panisch zu Tiefstkursen. <strong>Zeit zum antizyklischen Einkaufen.</strong></p>
        </div>

        <div class="text-center">
          <div class="w-16 h-16 bg-blue-100 border-4 border-blue-300 rounded-full mx-auto mb-4 flex items-center justify-center">
            <span class="text-2xl">⚖️</span>
          </div>
          <h5 class="font-bold text-blue-700 mb-2">Stimmungswechsel</h5>
          <p class="text-sm text-blue-600">Kurse stabilisieren sich, „starke Hände" haben gekauft und warten ab. <strong>Phase der Geduld.</strong></p>
        </div>

        <div class="text-center">
          <div class="w-16 h-16 bg-green-100 border-4 border-green-300 rounded-full mx-auto mb-4 flex items-center justify-center">
            <span class="text-2xl">📈</span>
          </div>
          <h5 class="font-bold text-green-700 mb-2">Übertreibungs-Phase</h5>
          <p class="text-sm text-green-600">Das „Publikum" kehrt zurück, Gier und Euphorie treiben die Kurse. <strong>Zeit für „starke Hände" zu verkaufen.</strong></p>
        </div>
      </div>
    </div>

    <p>Während die moderne Finanztheorie oft versucht, den Markt linear zu prognostizieren, sah Kostolany die Börse als ein System, das sich durch Auf- und Abwärtstrends bewegt, die von <strong>menschlicher Psychologie</strong> getrieben werden.</p>

    <div class="bg-amber-50 border border-amber-200 p-6 rounded-lg my-8">
      <h4 class="font-semibold text-amber-800 mb-3">🧠 Die Psychologie hinter dem Ei</h4>
      <p class="text-amber-700 mb-3">Kostolanys Art der Börsenanalyse basiert auf dem Verständnis der <strong>Massenpsychologie</strong>:</p>
      <ul class="space-y-2 text-amber-700">
        <li>• <strong>Zahlen (Fundamentalanalyse) sind nicht alles</strong></li>
        <li>• <strong>Stimmungslagen</strong> treiben oft irrationale Kursbewegungen</li>
        <li>• <strong>Die beste Kauf- und Verkaufsgelegenheiten</strong> entstehen aus der Irrationalität der Masse</li>
        <li>• <strong>Timing ist eine Kunst</strong>, die psychologisches Verständnis erfordert</li>
      </ul>
    </div>

    <p>Ohne dieses psychologische Verständnis verpasst ein rein rationaler Investor die besten Gelegenheiten, die sich aus der Irrationalität der Masse ergeben. Er kann die „Kunst" des Timings nicht beherrschen, weil er die „Wissenschaft" der irrationalen Märkte nicht versteht.</p>

    <blockquote class="border-l-4 border-primary pl-6 italic text-lg text-joerg-700 my-6">
      "An der Börse sind zwei mal zwei niemals vier, sondern fünf minus eins. Man muss nur das minus eins herausfinden."
      <footer class="text-sm text-joerg-600 mt-2">— André Kostolany</footer>
    </blockquote>
  `
};